import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import { get, times, isNil } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { PhotoflipperModel, buildMediaCountsPath } from 'client/data/models/photoflipper';
import { getPagesCount } from 'client/site-modules/pictures/utils/gallery-utils';
import {
  GallerySection,
  PlaceholderRow,
} from 'client/site-modules/pictures/components/gallery-section/gallery-section';
import { IntersectionObserver } from 'client/utils/intersection-observer';

export function GalleryUI({ params, totalSectionsCount = null }) {
  const [visibleSectionsCount, setVisibleSectionsCount] = useState(1);
  const [prevCategory, setPrevCategory] = useState(params.category);
  const loader = useRef(null);

  const loadMore = useCallback(
    entries => {
      const target = entries[0];
      if (target.isIntersecting && visibleSectionsCount < totalSectionsCount) {
        setVisibleSectionsCount(visibleSectionsCount + 1);
      }
    },
    [visibleSectionsCount, totalSectionsCount]
  );

  useEffect(() => {
    const loaderEl = loader && loader.current;

    const options = {
      root: null,
      rootMargin: '250px',
      threshold: 1,
    };

    const observer = IntersectionObserver && new IntersectionObserver(loadMore, options);

    if (IntersectionObserver && loaderEl) {
      observer.observe(loaderEl);
    }

    return () => IntersectionObserver && loaderEl && observer.unobserve(loaderEl);
  }, [loader, loadMore]);

  if (params.category !== prevCategory) {
    setVisibleSectionsCount(1);
    setPrevCategory(params.category);
  }

  if (isNil(totalSectionsCount)) {
    return (
      <div className="gallery py-1">
        <Row>
          <PlaceholderRow />
        </Row>
      </div>
    );
  }

  return (
    <div className="gallery py-1">
      {totalSectionsCount === 0 ? (
        'Sorry, no pictures available. Please check back later.'
      ) : (
        <Row>
          {times(visibleSectionsCount, ind => (
            <GallerySection params={params} pageNum={ind + 1} key={ind} />
          ))}
        </Row>
      )}
      <div ref={loader} />
    </div>
  );
}

GalleryUI.propTypes = {
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
    submodel: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
  totalSectionsCount: PropTypes.number,
};

export const stateToPropsConfig = {
  totalSectionsCount: bindToPath(
    ({ params }) => buildMediaCountsPath(params),
    PhotoflipperModel,
    (photosCounts, { params }) => getPagesCount(get(photosCounts, params.category, 0))
  ),
};

export const Gallery = connectToModel(GalleryUI, stateToPropsConfig);
